import { useEffect } from "react";
import { Link, useRoute } from "wouter";
import Layout from "@/components/Layout";
import { useSEO } from "@/hooks/useSEO";
import { BLOG_YAZILARI } from "@/lib/blog_data";
import { ilgiliYazilar } from "@/lib/ilgili_yazilar";
import { MarkdownIcerik } from "@/lib/markdown";
import NotFound from "@/pages/NotFound";
import { Calendar, Clock, ArrowLeft } from "lucide-react";

export default function BlogDetail() {
  const [, params] = useRoute("/blog/:slug");
  const yazi = BLOG_YAZILARI.find(y => y.slug === params?.slug);

  const url = yazi ? `https://adsmanager.com.tr/blog/${yazi.slug}` : "";

  useSEO(
    yazi
      ? {
          title: `${yazi.title} | AdsManager Blog`,
          description: yazi.summary,
          canonical: url,
        }
      : undefined
  );

  // BlogPosting JSON-LD
  useEffect(() => {
    if (!yazi) return;
    const schema = {
      "@context": "https://schema.org",
      "@type": "BlogPosting",
      headline: yazi.title,
      description: yazi.summary,
      datePublished: yazi.isoDate,
      dateModified: yazi.isoDate,
      inLanguage: "tr",
      keywords: yazi.keywords.join(", "),
      mainEntityOfPage: url,
      author: { "@type": "Organization", name: "AdsManager", url: "https://adsmanager.com.tr" },
      publisher: {
        "@type": "Organization",
        "@id": "https://adsmanager.com.tr/#organization",
        name: "AdsManager",
        logo: { "@type": "ImageObject", url: "https://adsmanager.com.tr/adsmanager-logo.png" },
      },
    };
    const el = document.createElement("script");
    el.id = "blog-json-ld";
    el.type = "application/ld+json";
    el.textContent = JSON.stringify(schema);
    document.getElementById("blog-json-ld")?.remove();
    document.head.appendChild(el);
    return () => document.getElementById("blog-json-ld")?.remove();
  }, [yazi, url]);

  if (!yazi) return <NotFound />;

  return (
    <Layout>
      <article className="bg-[#FFFDF7]">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-14 lg:py-20">
          <Link href="/blog" className="flex w-fit items-center gap-2 py-2 -my-2 text-sm font-bold text-[#0A1A3F]/60 hover:text-[#6B8AFE] transition-colors mb-6 sm:mb-8">
            <ArrowLeft className="w-4 h-4" /> Tüm Yazılar
          </Link>

          <span className="block w-fit bg-[#6B8AFE] text-white border-2 border-[#0A1A3F] px-2.5 py-1 text-xs font-bold uppercase tracking-wider mb-5 shadow-[2px_2px_0_0_#0A1A3F]">
            {yazi.category}
          </span>
          <h1 className="text-[1.75rem] sm:text-3xl lg:text-4xl font-black text-[#0A1A3F] tracking-tight leading-tight mb-5 break-words">
            {yazi.title}
          </h1>
          <div className="flex items-center flex-wrap gap-x-5 gap-y-2 text-xs font-bold text-[#0A1A3F]/50 mb-8 sm:mb-10 pb-6 border-b-3 border-[#0A1A3F]">
            <span className="flex items-center gap-1.5"><Calendar className="w-4 h-4" />{yazi.date}</span>
            <span className="flex items-center gap-1.5"><Clock className="w-4 h-4" />{yazi.readTime} okuma</span>
          </div>

          <MarkdownIcerik content={yazi.content} />

          {/* İlgili yazılar: her yazı en az üç yazıdan bağlantı alır */}
          <nav aria-label="İlgili yazılar" className="mt-12 sm:mt-14">
            <h2 className="font-black text-xl text-[#0A1A3F] mb-4">İlgili Yazılar</h2>
            <ul className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              {ilgiliYazilar(yazi.slug).map(y => (
                <li key={y.slug}>
                  <Link href={`/blog/${y.slug}`} className="block h-full bg-white border-3 border-[#0A1A3F] p-4 shadow-[3px_3px_0_0_#6B8AFE] hover:translate-x-[1px] hover:translate-y-[1px] transition-all">
                    <span className="block text-[11px] font-black uppercase text-[#6B8AFE] mb-1">{y.category}</span>
                    <span className="block text-sm font-bold text-[#0A1A3F] leading-snug">{y.title}</span>
                  </Link>
                </li>
              ))}
            </ul>
          </nav>

          {/* CTA */}
          <div className="mt-12 sm:mt-14 bg-[#0A1A3F] text-white border-3 border-[#0A1A3F] p-5 sm:p-7 shadow-[6px_6px_0_0_#6B8AFE]">
            <h3 className="font-black text-xl text-[#6B8AFE] mb-3">Reklam hesabınızla ilgili destek mi lazım?</h3>
            <p className="text-sm text-white/70 font-medium leading-relaxed mb-5">
              Yüksek limitli ajans hesapları, reklam yönetimi ve platform çözümlerimizle tanışın —
              durumunuzu 15 dakikada ücretsiz analiz edelim.
            </p>
            <div className="flex flex-col sm:flex-row sm:flex-wrap gap-3 sm:gap-4">
              <Link href="/facebook-reklam-hesabi-kiralama" className="bg-[#6B8AFE] text-white border-2 border-white px-5 py-3 sm:py-2.5 font-bold text-sm text-center shadow-[2px_2px_0_0_#fff] hover:translate-x-[1px] hover:translate-y-[1px] transition-all">
                Ajans Hesabı Kiralama
              </Link>
              <a href="https://wa.me/905320522545?text=Merhaba%2C%20reklam%20hesab%C4%B1m%20i%C3%A7in%20destek%20almak%20istiyorum." target="_blank" rel="noopener noreferrer" className="bg-white text-[#0A1A3F] border-2 border-white px-5 py-3 sm:py-2.5 font-bold text-sm text-center shadow-[2px_2px_0_0_#6B8AFE] hover:translate-x-[1px] hover:translate-y-[1px] transition-all">
                WhatsApp'tan Yazın
              </a>
            </div>
          </div>
        </div>
      </article>
    </Layout>
  );
}
